import "./conversation.css";
import {useEffect, useRef, useState} from "react";
import { registerEndChat, unregisterEndChat } from '../helpers/endChatUtils';

// Import children components to plug in and render.
import MessagingHeader from "./MessagingHeader";
import MessagingBody from "./MessagingBody";
import MessagingInputFooter from "./MessagingInputFooter";

import {
    setJwt,
    setLastEventId,
    storeConversationId,
    getConversationId,
    getJwt,
    clearInMemoryData,
    setDeploymentConfiguration,
    getSalesforceMessagingUrl, getDeploymentDeveloperName
} from "../services/dataProvider";
import { subscribeToEventSource, closeEventSource } from '../services/eventSourceService';
import { sendTypingIndicator, sendTextMessage, getContinuityJwt, listConversations, listConversationEntries, closeConversation, getUnauthenticatedAccessToken, createConversation } from "../services/messagingService";
import * as ConversationEntryUtil from "../helpers/conversationEntryUtil";
import { CONVERSATION_CONSTANTS, STORAGE_KEYS, CLIENT_CONSTANTS, INTEGRATION_EVENT_TYPES, MESSAGE_EVENT_TYPES } from "../helpers/constants";
import { setItemInWebStorage, clearWebStorage } from "../helpers/webstorageUtils";
import { util } from "../helpers/common";
import { prechatUtil } from "../helpers/prechatUtil.js";
import Prechat from "./Prechat.js";
import CountdownTimer from "../helpers/countdownTimer.js";
import {
    storeOrganizationId,
    storeDeploymentDeveloperName,
    storeSalesforceMessagingUrl,
} from "../services/dataProvider";
import { dispatchEvent } from "../helpers/eventManager";

export default function Conversation(props) {
    // Initialize a list of conversation entries.
    const [conversationEntries, setConversationEntries] = useState([]);
    // Initialize the conversation status.
    const [conversationStatus, setConversationStatus] = useState(CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION);
    // Tracks whether Pre-Chat form should be shown.
    const [showPrechatForm, setShowPrechatForm] = useState(null);
    // Tracks the most recent conversation message that was failed to send.
    const [failedMessage, setFailedMessage] = useState(undefined);
    // Stores default values for pre-chat form from query params.
    const [prechatDefaultValues, setPrechatDefaultValues] = useState({});

    // Initialize current participants that are actively typing (except end user).
    // Each key is the participant's `senderName` or `role` and each value is a reference to a CountdownTimer object.
    const [currentTypingParticipants, setCurrentTypingParticipants] = useState({});
    // Initialize whether at least 1 participant (not including end user) is typing.
    const [isAnotherParticipantTyping, setIsAnotherParticipantTyping] = useState(false);

    const [isSSEConnected, setIsSSEConnected] = useState(false);

    const hasInitialized = useRef(false);


    useEffect(() => {

        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const initializeConversation = async () => {
            try {
                if (props.isExistingConversation) {
                    await handleExistingConversation();
                } else {
                    await handleNewConversation();
                }

                // await handleSubscribeToEventSource();
                props.uiReady(true);
            } catch (err) {
                console.error("Initialization failed:", err);
                props.showMessagingWindow(false);
            }
        };

        initializeConversation();

        const handleEndChat = () => {
                const baseUrl = getSalesforceMessagingUrl();
                const developerName = getDeploymentDeveloperName();
                const conversationId = getConversationId();

                const url = `${baseUrl}/iamessage/api/v2/conversation/${conversationId}?esDeveloperName=${developerName}`;

                fetch(url, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${getJwt()}`,
                        'Content-Type': 'application/json'
                    },
                    keepalive: true
                }).then(() => {
                    updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION);
                }).finally(() => {
                    cleanupMessagingData();
                });
        };

        // Enregistrer la fonction endChat globalement
        registerEndChat(handleEndChat);

        return () => {
            unregisterEndChat();
            if (isSSEConnected) {
                closeEventSource();
                setIsSSEConnected(false);
            }
        };
    }, []);

    useEffect(() => {
        const handleUnload = () => {
            if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
                const baseUrl = getSalesforceMessagingUrl();
                const developerName = getDeploymentDeveloperName();
                const conversationId = getConversationId();

                const url = `${baseUrl}/iamessage/api/v2/conversation/${conversationId}?esDeveloperName=${developerName}`;

                fetch(url, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${getJwt()}`,
                        'Content-Type': 'application/json'
                    },
                    keepalive: true
                });
            }
        };

        window.addEventListener("unload", handleUnload);
        return () => {
            window.removeEventListener("unload", handleUnload);
        };
    }, [conversationStatus]);


    /**
     * Update conversation status state based on the event from a child component i.e. MessagingHeader.
     * Updating conversation status state re-renders the current component as well as the child components and the child components can reactively use the updated conversation status to make any changes.
     *
     * @param {string} status - e.g. CLOSED.
     */
    function updateConversationStatus(status) {
        setConversationStatus(status);
        window.conversationStatus = status;
    }

    /**
     * Extracts prechat field values from URL query parameters
     * @returns {Object} Object with field names as keys and their values
     */
    function extractPrechatDataFromQueryParams() {
        const urlParams = new URLSearchParams(window.location.search);

        const prechatData = {
            _firstName: urlParams.get("firstname") || "",
            _lastName: urlParams.get("lastname") || "",
            _email: urlParams.get("email") || "",
            Source: urlParams.get("source") || "",
            ProductVersion: urlParams.get("productVersion") || ""
        };

        // Filter empty values to only pass what is provided
        return Object.fromEntries(
            Object.entries(prechatData).filter(([_, value]) => value !== "")
        );
    }

    /**
     * Handles a new conversation.
     *
     * 1. Fetch an Unauthenticated Access Token i.e. Messaging JWT.
     * 2. Create a new conversation.
     * @returns {Promise}
     */
    function handleNewConversation() {
        const newId = util.generateUUID();
        storeConversationId(newId);

        // Configuration déjà chargée via loadDeploymentConfig() dans index.js
        // Pas besoin de hardcoder l'URL ici

        return handleGetUnauthenticatedJwt(true)
            .then(() => {
                // Extract query params to pre-fill the form
                const queryParamValues = extractPrechatDataFromQueryParams();
                setPrechatDefaultValues(queryParamValues);

                // Show the pre-chat form if configured
                if (prechatUtil.shouldDisplayPrechatForm()) {
                    setShowPrechatForm(true);
                } else {
                    setShowPrechatForm(false);
                }
            })
            .catch((err) => {
                console.error("Error in handleNewConversation:", err);
                props.showMessagingWindow(false);
            });
    }

    /**
     * Handles an existing conversation.
     *
     * 1. Fetch a Continuation Access Token i.e. Messaging JWT.
     * 2. Lists the available conversations and loads the current (also most-recent) conversation that is OPEN.
     * 3. Fetch the entries for the current conversation.
     * @returns {Promise}
     */
    function handleExistingConversation() {
        return handleGetContinuityJwt()
                .then(() => {
                    return handleListConversations()
                        .then(() => {
                            return handleListConversationEntries()
                                .then(() => {
                                    return handleSubscribeToEventSource(); // ← ICI
                                })
                                .catch(err => {
                                    console.error(`${err}`);
                                });
                        })
                        .catch(err => {
                            console.error(`${err}`);
                            });
                })
                .catch(err => {
                    console.error(`${err}`);
                });
    }

    /**
     * Handles fetching an Unauthenticated Access Token i.e. Messaging JWT.
     *
     * 1. If a JWT already exists, simply return.
     * 2. Makes a request to Unauthenticated Access Token endpoint.
     * 3. Updates the web storage with the latest JWT.
     * 4. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleGetUnauthenticatedJwt(forceNew = false) {
        if (!forceNew && getJwt()) {
            return Promise.resolve(); // ← NE FAIS PAS de `handleExistingConversation()` ici !
        }

        return getUnauthenticatedAccessToken()
                .then((response) => {
                    // Parse the response object which includes access-token (JWT), configutation data.
                    if (typeof response === "object") {
                        setJwt(response.accessToken);
                        setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
                        setLastEventId(response.lastEventId);
                        setDeploymentConfiguration(response.context && response.context.configuration && response.context.configuration.embeddedServiceConfig);
                    }    
                })
                .catch((err) => {
                    console.error(`Something went wrong in fetching an Unauthenticated access token: ${err && err.message ? err.message : err}`);
                    handleMessagingErrors(err);
                    cleanupMessagingData();
                    props.showMessagingWindow(false);
                    throw new Error("Failed to fetch an Unauthenticated access token.");
                });
    }

    /**
     * Handles creating a new conversation.
     *
     * 1. If a conversation is already open, simply return.
     * 2. Generate a new unique conversation-id and initialize in-memory.
     * 3. Makes a request to Create Conversation endpoint.
     * 4. Updates the conversation status internally to OPENED, for the associated components to reactively update.
     * 5. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleCreateNewConversation(routingAttributes) {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
            return Promise.reject();
        }

        // Initialize a new unique conversation-id in-memory.
        // ICI CREATE CONVERSATION============================
        const newConversationId = util.generateUUID();
        storeConversationId(newConversationId);

        return handleGetUnauthenticatedJwt(true)
            .then(() => {
                return createConversation(getConversationId(), routingAttributes);
            })
            .then(() => {
                updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION);
                props.showMessagingWindow(true);
                props.uiReady(true);
                setShowPrechatForm(false);
                handleSubscribeToEventSource();
            })
            .catch((err) => {
                console.error(`Failed to create a new conversation with ID ${getConversationId()}: ${err?.message || err}`);
                handleMessagingErrors(err);
                cleanupMessagingData();
                props.showMessagingWindow(false);
                throw new Error("Failed to create a new conversation.");
            });
    }

    /**
     * Handles fetching a Continuation Access Token i.e. Messaging JWT.
     *
     * 1. Makes a request to Continuation Access Token endpoint.
     * 2. Updates the web storage with the latest JWT.
     * 3. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleGetContinuityJwt() {
        return getContinuityJwt()
                .then((response) => {
                    setJwt(response.accessToken);
                    setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
                })
                .catch((err) => {
                    console.error(`Something went wrong in fetching a Continuation Access Token: ${err && err.message ? err.message : err}`);
                    handleMessagingErrors(err);
                    throw new Error("Failed to fetch a Continuation access token.");
                });
    }

    /**
     * Handles fetching a list of all conversations available. This returns only conversations which are OPEN, unless otherwise specified in the request.
     *
     * 1. Makes a request to List Conversations endpoint.
     * 2. If there are multiple OPEN conversations, loads the conversation with the most-recent start time.
     * 3. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleListConversations() {
        return listConversations()
                .then((response) => {
                    if (response && response.openConversationsFound > 0 && response.conversations.length) {
                        const openConversations = response.conversations;
                        if (openConversations.length > 1) {
				            openConversations.sort((conversationA, conversationB) => conversationB.startTimestamp - conversationA.startTimestamp);
                        }
                        // Update conversation-id with the one from service.
                        storeConversationId(openConversations[0].conversationId);
                        updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION);
                        props.showMessagingWindow(true);
                    } else {
                        // No open conversations found.
                        cleanupMessagingData();
                        props.showMessagingWindow(false);
                    }
                })
                .catch((err) => {
                    console.error(`Something went wrong in fetching a list of conversations: ${err && err.message ? err.message : err}`);
                    handleMessagingErrors(err);
                    throw new Error("Failed to list the conversations.");
                });
    }

    /**
     * Handles fetching a list of all conversation entries for the current conversation.
     *
     * 1. Makes a request to List Conversation Entries endpoint.
     * 2. Renders the conversation entries based on their Entry Type.
     * @returns {Promise}
     */
    function handleListConversationEntries() {
        return listConversationEntries(getConversationId())
                .then((response) => {
                    if (Array.isArray(response)) {
                        response.reverse().forEach(entry => {
                            const conversationEntry = generateConversationEntryForCurrentConversation(entry);
                            if (!conversationEntry) {
                                return;
                            }
    
                            switch (conversationEntry.entryType) {
                                case CONVERSATION_CONSTANTS.EntryTypes.CONVERSATION_MESSAGE:
                                    conversationEntry.isEndUserMessage = ConversationEntryUtil.isMessageFromEndUser(conversationEntry);
                                    addConversationEntry(conversationEntry);
                                    break;
                                case CONVERSATION_CONSTANTS.EntryTypes.PARTICIPANT_CHANGED:
                                case CONVERSATION_CONSTANTS.EntryTypes.ROUTING_RESULT:
                                    addConversationEntry(conversationEntry);
                                    break;
                                default:
                            }
                        });
                    } else {
                        console.error(`Expecting a response of type Array from listConversationEntries but instead received: ${response}`);
                    }
                })
                .catch((err) => {
                    console.error(`Something went wrong while processing entries from listConversationEntries response:  ${err && err.message ? err.message : err}`);
                    handleMessagingErrors(err);
                    throw new Error("Failed to list the conversation entries for the current conversation.");
                });
    }

    /**
     * Handles establishing a connection to the EventSource i.e. SSE.
     * Selectively listens to the supported events in the app by adding the corresponding event listeners.
     * Note: Update the list of events/event-listeners to add/remove support for the available events. Refer https://developer.salesforce.com/docs/service/messaging-api/references/about/server-sent-events-structure.html
     * @returns {Promise}
     */
    function handleSubscribeToEventSource() {

        if (isSSEConnected) {
            return Promise.resolve();
        }

        const eventListenerMap = {
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_MESSAGE]: handleConversationMessageServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_ROUTING_RESULT]: handleRoutingResultServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_PARTICIPANT_CHANGED]: handleParticipantChangedServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STARTED_INDICATOR]: handleTypingStartedIndicatorServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STOPPED_INDICATOR]: handleTypingStoppedIndicatorServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_DELIVERY_ACKNOWLEDGEMENT]: handleConversationDeliveryAcknowledgementServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_READ_ACKNOWLEDGEMENT]: handleConversationReadAcknowledgementServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_CLOSE_CONVERSATION]: handleCloseConversationServerSentEvent
        };

        return subscribeToEventSource(eventListenerMap)
            .then(() => {
                setIsSSEConnected(true);
            })
            .catch((err) => {
                setIsSSEConnected(false);
                throw new Error(err);
            });
    }

    /**
     * Generate a Conversation Entry object from the server sent event.
     *
     * 1. Create a Conversation Entry object from the parsed event data.
     * 2. Return the Conversation Entry if the conversationEntry is for the current conversation and undefined, otherwise.
     * @param {object} event - Event data payload from server-sent event.
     * @returns {object|undefined}
     */
    function generateConversationEntryForCurrentConversation(parsedEventData) {
        const conversationEntry = ConversationEntryUtil.createConversationEntry(parsedEventData);

        // Handle server sent events only for the current conversation
        if (parsedEventData.conversationId === getConversationId()) {
            return conversationEntry;
        }
        return undefined;
    }

    /**
     * Adds a Conversation Entry object to the list of conversation entries. Updates the state of the list of conversation entries for the component(s) to be updated in-turn, reactively.
     * @param {object} conversationEntry - entry object for the current conversation.
     */
    function addConversationEntry(conversationEntry) {
        // Check if we should merge this entry with the previous one (for "URL + text" split messages)
        if (conversationEntries.length > 0 && shouldMergeConsecutiveMessages(conversationEntry)) {
            mergeWithPreviousEntry(conversationEntry);
            return;
        }

        conversationEntries.push(conversationEntry);
        setConversationEntries([...conversationEntries]);
    }

    /**
     * Determines if the current entry should be merged with the previous entry.
     * Salesforce sometimes splits "URL + text" into 2 consecutive TextMessages.
     */
    function shouldMergeConsecutiveMessages(conversationEntry) {
        const lastEntry = conversationEntries[conversationEntries.length - 1];

        // Both must be text messages from the same sender
        const areBothText = ConversationEntryUtil.isTextMessage(lastEntry) &&
                           ConversationEntryUtil.isTextMessage(conversationEntry);
        const isSameSender = lastEntry.sender?.subject === conversationEntry.sender?.subject;

        if (!areBothText || !isSameSender) {
            return false;
        }

        // Messages must be sent within 500ms of each other
        const timeDiff = Math.abs(
            new Date(conversationEntry.transcriptedTimestamp).getTime() -
            new Date(lastEntry.transcriptedTimestamp).getTime()
        );
        const isConsecutive = timeDiff < 500;

        if (!isConsecutive) {
            return false;
        }

        // Check if last message is just a URL (needs text to follow)
        const lastText = ConversationEntryUtil.getTextMessageContent(lastEntry);
        const isLastOnlyUrl = /^https?:\/\/[^\s]+$/.test(lastText?.trim());

        return isLastOnlyUrl;
    }

    /**
     * Merges the current entry with the previous entry by updating the previous entry's text.
     */
    function mergeWithPreviousEntry(conversationEntry) {
        const lastIndex = conversationEntries.length - 1;
        const lastEntry = conversationEntries[lastIndex];

        // Get both texts
        const urlText = ConversationEntryUtil.getTextMessageContent(lastEntry);
        const additionalText = ConversationEntryUtil.getTextMessageContent(conversationEntry);

        // Create a new merged entry (immutable update for React)
        const mergedEntry = {
            ...lastEntry,
            content: {
                ...lastEntry.content,
                staticContent: {
                    ...lastEntry.content.staticContent,
                    text: `${urlText} ${additionalText}`
                }
            }
        };

        // Replace the last entry
        conversationEntries[lastIndex] = mergedEntry;
        setConversationEntries([...conversationEntries]);
    }

    /**
     * Handle a CONVERSATION_MESSAGE server-sent event.
     *
     * This includes:
     *  1. Parse, populate, and create ConversationEntry object based on its entry type
     *      NOTE: Skip processing CONVERSATION_MESSAGE if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
     *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
     *  3. Dispatches OnNewMessage event for external integrations (RDM)
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleConversationMessageServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            const conversationEntry = generateConversationEntryForCurrentConversation(parsedEventData);
            
            if (!conversationEntry) {
                return;
            }

            // Check if this message has pending file info stored locally
            // (Salesforce SSE doesn't include attachment details, so we retrieve from local storage)
            if (conversationEntry.isEndUserMessage || ConversationEntryUtil.isMessageFromEndUser(conversationEntry)) {
                const pendingFilesKey = `pendingFiles_${getConversationId()}`;
                const existingPending = sessionStorage.getItem(pendingFilesKey);
                
                if (existingPending) {
                    try {
                        const pendingFiles = JSON.parse(existingPending);
                        const matchingFile = pendingFiles.find(pf => pf.messageId === conversationEntry.messageId);
                        
                        if (matchingFile) {
                            // Add attachment info to the conversation entry
                            conversationEntry.attachments = [{
                                name: matchingFile.file.name,
                                mimeType: matchingFile.file.type,
                                size: matchingFile.file.size,
                                url: matchingFile.file.localUrl,
                                fileId: matchingFile.fileId
                            }];
                            
                            // Remove from pending files
                            const updatedPending = pendingFiles.filter(pf => pf.messageId !== conversationEntry.messageId);
                            if (updatedPending.length > 0) {
                                sessionStorage.setItem(pendingFilesKey, JSON.stringify(updatedPending));
                            } else {
                                sessionStorage.removeItem(pendingFilesKey);
                            }
                        }
                    } catch (e) {
                        console.error('Error parsing pending files from sessionStorage:', e);
                        // Clear corrupted data
                        sessionStorage.removeItem(pendingFilesKey);
                    }
                }
            }

            if (ConversationEntryUtil.isMessageFromEndUser(conversationEntry)) {
                conversationEntry.isEndUserMessage = true;

                // Since message is echoed back by the server, mark the conversation entry as sent.
                conversationEntry.isSent = true;
            } else {
                conversationEntry.isEndUserMessage = false;
            }

            // Check if this is a URL message that should be merged with the previous text message
            // Salesforce splits "text with url" into two separate messages: "text with " + "url"
            const isUrlOnlyMessage = conversationEntry.content?.staticContent?.text &&
                                     /^https?:\/\/[^\s]+$/.test(conversationEntry.content.staticContent.text.trim());

            if (isUrlOnlyMessage && conversationEntries.length > 0) {
                const lastEntry = conversationEntries[conversationEntries.length - 1];
                const isSameSender = lastEntry.actorType === conversationEntry.actorType;
                const isRecentMessage = Math.abs(lastEntry.transcriptedTimestamp - conversationEntry.transcriptedTimestamp) < 1000; // Within 1 second

                if (isSameSender && isRecentMessage && ConversationEntryUtil.isTextMessage(lastEntry)) {
                    // Merge the URL with the previous message
                    const urlToAppend = conversationEntry.content.staticContent.text;
                    const previousText = ConversationEntryUtil.getTextMessageContent(lastEntry);

                    // Update the last entry's text to include the URL
                    lastEntry.content.staticContent.text = previousText + urlToAppend;

                    // Trigger a re-render by creating a new array reference
                    setConversationEntries([...conversationEntries]);

                    return; // Don't add this as a separate message
                }
            }

            addConversationEntry(conversationEntry);

            // Dispatch OnNewMessage event for external integrations (RDM)
            dispatchNewMessageEvent(conversationEntry);
        } catch(err) {
            console.error(`Something went wrong in handling conversation message server sent event:`, err);
        }
    }

    /**
     * Helper function to dispatch OnNewMessage events with proper structure
     * @param {object} conversationEntry - The conversation entry to convert to event data
     */
    function dispatchNewMessageEvent(conversationEntry) {
        try {
            // Determine message type based on actor/sender
            let messageType = MESSAGE_EVENT_TYPES.SYSTEM_MESSAGE;

            if (conversationEntry.isEndUserMessage) {
                messageType = MESSAGE_EVENT_TYPES.USER_MESSAGE;
            } else if (conversationEntry.actorType === CONVERSATION_CONSTANTS.ParticipantRoles.AGENT) {
                messageType = MESSAGE_EVENT_TYPES.AGENT_MESSAGE;
            } else if (conversationEntry.actorType === CONVERSATION_CONSTANTS.ParticipantRoles.CHATBOT) {
                messageType = MESSAGE_EVENT_TYPES.CHATBOT_MESSAGE;
            }

            // Build event payload with comprehensive information for RDM
            const eventData = {
                eventType: INTEGRATION_EVENT_TYPES.ON_NEW_MESSAGE,
                messageType: messageType,
                timestamp: conversationEntry.transcriptedTimestamp || Date.now(),
                conversationId: conversationEntry.conversationId,
                messageId: conversationEntry.messageId,

                // Message content
                content: {
                    text: ConversationEntryUtil.getTextMessageContent(conversationEntry),
                    messageFormat: conversationEntry.content?.staticContent?.formatType,
                    messageContentType: conversationEntry.messageType
                },

                // Sender information
                sender: {
                    role: conversationEntry.actorType,
                    displayName: conversationEntry.actorName,
                    subject: conversationEntry.sender?.subject
                },

                // Additional metadata
                metadata: {
                    isSent: conversationEntry.isSent || false,
                    isDelivered: conversationEntry.isDelivered || false,
                    isRead: conversationEntry.isRead || false,
                    isEndUserMessage: conversationEntry.isEndUserMessage || false,
                    messageReason: conversationEntry.messageReason
                },

                // Raw conversation entry for advanced use cases
                raw: conversationEntry
            };

            dispatchEvent(INTEGRATION_EVENT_TYPES.ON_NEW_MESSAGE, eventData);
        } catch (error) {
            // Non-blocking: Log error but don't throw to avoid breaking the message flow
            console.error('[Conversation] Failed to dispatch OnNewMessage event:', {
                error,
                conversationId: conversationEntry?.conversationId,
                messageId: conversationEntry?.messageId,
                senderRole: conversationEntry?.actorType
            });
        }
    }

    /**
     * Handle a ROUTING_RESULT server-sent event.
     *
     * This includes:
     *  1. Parse, populate, and create ConversationEntry object based on its entry type.
     *      NOTE: Skip processing ROUTING_RESULT if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
     *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
     *
     *  NOTE: Update the chat client based on the latest routing result. E.g. if the routing type is transfer, set an internal flag like `isTransferring` to 'true' and use that to show a transferring indicator in the ui.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleRoutingResultServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            const conversationEntry = generateConversationEntryForCurrentConversation(parsedEventData);
            if (!conversationEntry) {
                return;
            }

            if (conversationEntry.messageType === CONVERSATION_CONSTANTS.RoutingTypes.INITIAL) {
                // Render reasonForNotRouting when initial routing fails.
                switch (conversationEntry.content.failureType) {
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.NO_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.SUBMISSION_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.ROUTING_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.UNKNOWN_ERROR:
                        addConversationEntry(conversationEntry);
                        break;
                    default:
                        console.error(`Unrecognized initial routing failure type: ${conversationEntry.content.failureType}`);
                }
                // Handle when a conversation is being transferred.
            } else if (conversationEntry.messageType === CONVERSATION_CONSTANTS.RoutingTypes.TRANSFER) {
                switch (conversationEntry.content.failureType) {
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.NO_ERROR:
                        // Render transfer timestamp when transfer is requested successfully.
                        // TODO: Add a transfer state ui update.
                        addConversationEntry(conversationEntry);
                        break;
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.SUBMISSION_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.ROUTING_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.UNKNOWN_ERROR:
                        break;
                    default:
                        console.error(`Unrecognized transfer routing failure type: ${conversationEntry.content.failureType}`);
                }
            } else {
                console.error(`Unrecognized routing type: ${conversationEntry.messageType}`);
            }
        } catch (err) {
            console.error(`Something went wrong in handling routing result server sent event: ${err}`);
        }
    }

    /**
     * Handle a PARTICIPANT_CHANGED server-sent event.
     *
     * This includes:
     *  1. Parse, populate, and create ConversationEntry object based on its entry type.
     *      NOTE: Skip processing PARTICIPANT_CHANGED if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
     *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
     *  3. Dispatches OnAgentLeft event when an agent leaves (without closing the conversation)
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleParticipantChangedServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            const conversationEntry = generateConversationEntryForCurrentConversation(parsedEventData);
            if (!conversationEntry) {
                return;
            }
            addConversationEntry(conversationEntry);

            // Check if this is an agent leaving event (not closing, just leaving)
            // This is important for transfer scenarios where agent leaves but conversation continues
            const isRemoveOperation = conversationEntry.messageType === CONVERSATION_CONSTANTS.ParticipantChangedOperations.REMOVE;
            const participantRole = conversationEntry.content?.entries?.[0]?.participant?.role;
            const isAgentLeaving = isRemoveOperation && participantRole === CONVERSATION_CONSTANTS.ParticipantRoles.AGENT;

            if (isAgentLeaving) {
                dispatchAgentLeftEvent(conversationEntry, parsedEventData);
            }
        } catch (err) {
            console.error(`Something went wrong in handling participant changed server sent event:`, err);
        }
    }

    /**
     * Helper function to dispatch OnAgentLeft events
     * @param {object} conversationEntry - The conversation entry for the participant change
     * @param {object} parsedEventData - Raw parsed event data
     */
    function dispatchAgentLeftEvent(conversationEntry, parsedEventData) {
        try {
            const participantInfo = conversationEntry.content?.entries?.[0];
            const agentDisplayName = participantInfo?.displayName || participantInfo?.participant?.role || 'Agent';

            const eventData = {
                eventType: INTEGRATION_EVENT_TYPES.ON_AGENT_LEFT,
                timestamp: conversationEntry.transcriptedTimestamp || Date.now(),
                conversationId: conversationEntry.conversationId,

                // Agent information
                agent: {
                    displayName: agentDisplayName,
                    role: participantInfo?.participant?.role,
                    subject: participantInfo?.participant?.subject
                },

                // Context information
                metadata: {
                    operation: CONVERSATION_CONSTANTS.ParticipantChangedOperations.REMOVE,
                    // This is important: agent left but conversation is still open (could be transfer)
                    isConversationStillOpen: true,
                    entryType: conversationEntry.entryType
                },

                // Raw data for advanced use cases
                raw: {
                    conversationEntry,
                    parsedEventData
                }
            };

            dispatchEvent(INTEGRATION_EVENT_TYPES.ON_AGENT_LEFT, eventData);
        } catch (error) {
            console.error('[Conversation] Failed to dispatch OnAgentLeft event:', {
                error,
                conversationId: conversationEntry?.conversationId
            });
        }
    }

    /**
     * Handle a TYPING_STARTED_INDICATOR server-sent event.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleTypingStartedIndicatorServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle typing indicators only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                const senderName = ConversationEntryUtil.getSenderDisplayName(parsedEventData) || ConversationEntryUtil.getSenderRole(parsedEventData);
                const senderRole = ConversationEntryUtil.getSenderRole(parsedEventData);
                const typingParticipantTimer = currentTypingParticipants[senderName] && currentTypingParticipants[senderName].countdownTimer;

                // If we have received typing indicator from this sender within the past 5 seconds, reset the timer
                // Otherwise, start a new timer
                if (senderRole !== CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER) {
                    if (typingParticipantTimer) {
                        typingParticipantTimer.reset(Date.now());
                    } else {
                        currentTypingParticipants[senderName] = {
                            countdownTimer: new CountdownTimer(() => {
                                delete currentTypingParticipants[senderName];

                                if (!Object.keys(currentTypingParticipants).length) {
                                    setIsAnotherParticipantTyping(false);
                                }
                            }, CLIENT_CONSTANTS.TYPING_INDICATOR_DISPLAY_TIMEOUT, Date.now()),
                            role: parsedEventData.conversationEntry.sender.role
                        };

                        currentTypingParticipants[senderName].countdownTimer.start();
                    }

                    setIsAnotherParticipantTyping(true);

                    // Dispatch OnNewMessage event for typing started (external integrations)
                    dispatchTypingIndicatorEvent(parsedEventData, MESSAGE_EVENT_TYPES.TYPING_STARTED, senderName, senderRole);
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling typing started indicator server sent event: ${err}`);
        }
    }

    /**
     * Handle a TYPING_STOPPED_INDICATOR server-sent event.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleTypingStoppedIndicatorServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle typing indicators only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                const senderName = ConversationEntryUtil.getSenderDisplayName(parsedEventData) || ConversationEntryUtil.getSenderRole(parsedEventData);
                const senderRole = ConversationEntryUtil.getSenderRole(parsedEventData);

                delete currentTypingParticipants[senderName];

                if (!Object.keys(currentTypingParticipants).length) {
                    setIsAnotherParticipantTyping(false);
                }

                // Dispatch OnNewMessage event for typing stopped (external integrations)
                if (senderRole !== CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER) {
                    dispatchTypingIndicatorEvent(parsedEventData, MESSAGE_EVENT_TYPES.TYPING_STOPPED, senderName, senderRole);
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling typing stopped indicator server sent event: ${err}`);
        }
    }

    /**
     * Helper function to dispatch typing indicator events
     * @param {object} parsedEventData - Parsed event data from SSE
     * @param {string} typingEventType - Either TYPING_STARTED or TYPING_STOPPED
     * @param {string} senderName - Display name of the sender
     * @param {string} senderRole - Role of the sender
     */
    function dispatchTypingIndicatorEvent(parsedEventData, typingEventType, senderName, senderRole) {
        try {
            const eventData = {
                eventType: INTEGRATION_EVENT_TYPES.ON_NEW_MESSAGE,
                messageType: typingEventType,
                timestamp: Date.now(),
                conversationId: parsedEventData.conversationId,
                messageId: null, // No message ID for typing indicators

                // No actual content for typing indicators
                content: {
                    text: '',
                    messageFormat: null,
                    messageContentType: 'TypingIndicator'
                },

                // Sender information
                sender: {
                    role: senderRole,
                    displayName: senderName,
                    subject: parsedEventData.conversationEntry?.sender?.subject
                },

                // Metadata
                metadata: {
                    isTypingIndicator: true,
                    typingStatus: typingEventType === MESSAGE_EVENT_TYPES.TYPING_STARTED ? 'started' : 'stopped'
                },

                // Raw data for advanced use cases
                raw: parsedEventData
            };

            dispatchEvent(INTEGRATION_EVENT_TYPES.ON_NEW_MESSAGE, eventData);
        } catch (error) {
            console.error('[Conversation] Failed to dispatch typing indicator event:', {
                error,
                typingEventType,
                senderName,
                senderRole,
                conversationId: parsedEventData?.conversationId
            });
        }
    }

    /**
     * Handle a server-sent event CONVERSATION_DELIVERY_ACKNOWLEDGEMENT:
     * - Parse server-sent event and update a conversation entry (if it exists) with data from the event.
     * - Store delivery acknowledgement timestamp on conversation entry to show receipt timestamp.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleConversationDeliveryAcknowledgementServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle delivery acknowledgements only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                if (parsedEventData.conversationEntry && parsedEventData.conversationEntry.relatedRecords.length && parsedEventData.conversationEntry.entryPayload.length) {
                    // Store acknowledgement timestamp and identifier of message that was delivered.
                    const deliveredMessageId = parsedEventData.conversationEntry.relatedRecords[0];
                    const deliveryAcknowledgementTimestamp = JSON.parse(parsedEventData.conversationEntry.entryPayload).acknowledgementTimestamp;

                    // Make deep copy of conversation entries to find matching messageId. 
                    util.createDeepCopy(conversationEntries).filter((conversationEntry) => {
                        if (conversationEntry.messageId === deliveredMessageId) {
                            conversationEntry.isDelivered = true;
                            conversationEntry.deliveryAcknowledgementTimestamp = deliveryAcknowledgementTimestamp;
                        }
                    });
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling conversation delivery acknowledgement server sent event: ${err}`);
        }
    }

    /**
     * Handle a server-sent event CONVERSATION_READ_ACKNOWLEDGEMENT:
     * - Parse server-sent event and update a conversation entry (if it exists) with data from the event.
     * - Store read acknowledgement timestamp on conversation entry to show receipt timestamp.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleConversationReadAcknowledgementServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle read acknowledgements only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                if (parsedEventData.conversationEntry && parsedEventData.conversationEntry.relatedRecords.length && parsedEventData.conversationEntry.entryPayload.length) {
                    // Store acknowledgement timestamp and identifier of message that was read.
                    const readMessageId = parsedEventData.conversationEntry.relatedRecords[0];
                    const readAcknowledgementTimestamp = JSON.parse(parsedEventData.conversationEntry.entryPayload).acknowledgementTimestamp;

                    // Make deep copy of conversation entries to find matching messageId. 
                    util.createDeepCopy(conversationEntries).filter((conversationEntry) => {
                        if (conversationEntry.messageId === readMessageId) {
                            conversationEntry.isRead = true;
                            conversationEntry.readAcknowledgementTimestamp = readAcknowledgementTimestamp;
                        }
                    });
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling conversation read acknowledgement server sent event: ${err}`);
        }
    }

    /**
     * Handle a CONVERSATION_CLOSED server-sent event.
     *
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleCloseConversationServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);

            // Do not render conversation ended text if the conversation entry is not for the current conversation.
            if (getConversationId() === parsedEventData.conversationId) {
                // Dispatch OnClose event BEFORE updating status
                // This allows RDM integration to capture the close event with full context
                dispatchConversationCloseEvent(parsedEventData);

                // Update state to conversation closed status.
                updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION);
            }
        } catch (err) {
            console.error(`Something went wrong while handling conversation closed server sent event in conversation ${getConversationId()}: ${err}`);
        }
    }

    /**
     * Helper function to dispatch OnClose events
     * @param {object} parsedEventData - Parsed event data from SSE
     */
    function dispatchConversationCloseEvent(parsedEventData) {
        try {
            // Extract close reason from the event payload if available
            const entryPayload = parsedEventData.conversationEntry?.entryPayload
                ? JSON.parse(parsedEventData.conversationEntry.entryPayload)
                : {};

            const eventData = {
                eventType: INTEGRATION_EVENT_TYPES.ON_CLOSE,
                timestamp: parsedEventData.conversationEntry?.transcriptedTimestamp || Date.now(),
                conversationId: parsedEventData.conversationId,

                // Close reason and context
                closeInfo: {
                    reason: entryPayload.reason || 'unknown',
                    // Possible reasons: agent_closed, api_closed, timeout, idle, bot_closed, etc.
                    closedBy: entryPayload.closedBy || 'unknown',
                    closeType: entryPayload.closeType || 'standard'
                },

                // Conversation metadata at time of close
                metadata: {
                    // Current conversation status before close
                    previousStatus: conversationStatus,
                    // Any additional context from the entry payload
                    entryType: entryPayload.entryType,
                    // Indicates this is a complete conversation closure (not just agent leaving)
                    isCompleteClose: true
                },

                // Raw data for advanced use cases
                raw: parsedEventData
            };

            dispatchEvent(INTEGRATION_EVENT_TYPES.ON_CLOSE, eventData);
        } catch (error) {
            console.error('[Conversation] Failed to dispatch OnClose event:', {
                error,
                conversationId: parsedEventData?.conversationId
            });
        }
    }

    /**
     * Handle sending a static text message in a conversation.
     * @param {string} conversationId - Identifier of the conversation.
     * @param {string} value - Actual text of the message.
     * @param {string} messageId - Unique identifier of the message.
     * @param {string} inReplyToMessageId - Identifier of another message where this message is being replied to.
     * @param {boolean} isNewMessagingSession - Whether it is a new messaging session.
     * @param {object} routingAttributes - Pre-Chat fields and its values, if configured.
     * @param {object} language - language code.
     * 
     * @returns {Promise}
     */
    function handleSendTextMessage(conversationId, value, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language) {
        return sendTextMessage(conversationId, value, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language)
                .catch((err) => {
                    console.error(`Something went wrong while sending a message to conversation ${conversationId}: ${err}`);
                    setFailedMessage(Object.assign({}, {messageId, value, inReplyToMessageId, isNewMessagingSession, routingAttributes, language}));
                    handleMessagingErrors(err);
                });
    }

    /**
     * Close messaging window handler for the event from a child component i.e. MessagingHeader.
     * When such event is received, invoke the parent's handler to close the messaging window if the conversation status is closed or not yet started.
     */
    function endConversation() {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
            // End the conversation if it is currently opened.
            return closeConversation(getConversationId())
                .catch((err) => {
                    console.error(`Something went wrong in closing the conversation with conversation-id ${getConversationId()}: ${err}`);
                })
                .finally(() => {
                    cleanupMessagingData();
                });
        }
    }

    /**
     * Close messaging window handler for the event from a child component i.e. MessagingHeader.
     * When such event is received, invoke the parent's handler to close the messaging window if the conversation status is closed or not yet started.
     */
    function closeMessagingWindow() {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION || conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION) {
            props.showMessagingWindow(false);
        }
    }

    /**
     * Performs a cleanup in the app.
     * 1. Closes the EventSource connection.
     * 2. Clears the web storage.
     * 3. Clears the in-memory messaging data.
     * 4. Update the internal conversation status to CLOSED.
     */
    function cleanupMessagingData() {
        closeEventSource()
        .catch((err) => {
            console.error(`Something went wrong in closing the Event Source (SSE): ${err}`);
        });

        // Clean up pending file URLs to prevent memory leaks
        const conversationId = getConversationId();
        if (conversationId) {
            const pendingFilesKey = `pendingFiles_${conversationId}`;
            const existingPending = sessionStorage.getItem(pendingFilesKey);
            
            if (existingPending) {
                try {
                    const pendingFiles = JSON.parse(existingPending);
                    // Revoke object URLs to free memory
                    pendingFiles.forEach(pf => {
                        if (pf.file?.localUrl) {
                            URL.revokeObjectURL(pf.file.localUrl);
                        }
                    });
                } catch (e) {
                    console.error('Error cleaning up pending files:', e);
                }
                sessionStorage.removeItem(pendingFilesKey);
            }
        }

        clearWebStorage();
        clearInMemoryData();

        // Update state to conversation closed status.
        updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION);
    }

    /**
     * Handles the errors from messaging endpoint requests.
     * If a request is failed due to an Unauthorized error (i.e. 401), peforms a cleanup and resets the app and console logs otherwise.
     */
    function handleMessagingErrors(err) {
        if (typeof err === "object") {
            if (err.status) {
                switch (err.status) {
                    case 401:
                        console.error(`Unauthenticated request: ${err.message}`);
                        cleanupMessagingData();
                        props.showMessagingWindow(false);
                        break;
                    case 400:
                        console.error(`Invalid request parameters. Please check your data before retrying: ${err.message}`);
                        break;
                    case 404:
                        console.error(`Resource not found. Please check your data before retrying: ${err.message}`);
                        break;
                    case 429:
                        console.warn(`Too many requests issued from the app. Try again in sometime: ${err.message}`);
                        break;
                    /**
                     * HTTP error code returned by the API(s) when a message is sent and failed because no messaging session exists. This error indicates client
                     * to surface the Pre-Chat form if configured to show for every new messaging session and then retry the failed message with routingAttributes.
                     */
                    case 417:
                        if (prechatUtil.shouldDisplayPrechatForm() && prechatUtil.shouldDisplayPrechatEveryMessagingSession()) {
                            setShowPrechatForm(true);
                        }
                    case 500:
                        console.error(`Something went wrong in the request, please try again: ${err.message}`);
                        break;
                    default:
                        console.error(`Unhandled/Unknown http error: ${err}`);
                        cleanupMessagingData();
                        props.showMessagingWindow(false);
                }
                return;
            }
            console.error(`Something went wrong: ${err && err.message ? err.message : err}`);
        }
        return;
    }

    /**
     * Handles submitting a Pre-Chat form to either start a new conversation or a new messaging session.
     * @param {object} prechatData - Object containing key-value pairs of Pre-Chat form fields and their corresponding values.
     * @returns {Promise} Promise that resolves when the conversation is created or rejects on error
     */
    function handlePrechatSubmit(prechatData) {
        const urlParams = new URLSearchParams(window.location.search);
        const mergedPrechat = prechatUtil.cleanRoutingAttributes({
            ...prechatData,
            Source: urlParams.get("source") ?? "",
            ProductVersion: urlParams.get("productVersion") ?? "",
        });

        let prechatSubmitPromise;
        // If there is a failed message being tracked while submitting Pre-Chat form, consider it is an existing conversation but a new messaging session. Resend the failed message with the routing attributes to begin a new messaging session.
        if (failedMessage) {
            prechatSubmitPromise = handleSendTextMessage(getConversationId(), failedMessage.value, failedMessage.messageId, failedMessage.inReplyToMessageId, true, prechatData, failedMessage.language);
        } else {
            // Configuration déjà chargée via loadDeploymentConfig() dans index.js
            // Pas besoin de hardcoder les valeurs ici

            // If there is no failed message being tracked while submitting Pre-Chat form, create a new conversation.
            prechatSubmitPromise = handleCreateNewConversation(mergedPrechat);
        }
        return prechatSubmitPromise
            .then(() => {
                setShowPrechatForm(false);
            })
            .catch((error) => {
                handleMessagingErrors(error);
                throw error;
            });
    }

    return (
        <div className="conversationContainer">

            {showPrechatForm === true && (
                <Prechat
                    prechatSubmit={handlePrechatSubmit}
                    defaultValues={prechatDefaultValues}
                />
            )}

            {showPrechatForm === false && (
                <>
                    <MessagingBody
                        conversationEntries={conversationEntries}
                        conversationStatus={conversationStatus}
                        typingParticipants={currentTypingParticipants}
                        showTypingIndicator={isAnotherParticipantTyping}
                    />
                    <MessagingInputFooter
                        conversationStatus={conversationStatus}
                        sendTextMessage={handleSendTextMessage}
                        sendTypingIndicator={sendTypingIndicator}
                    />
                </>
            )}

            {showPrechatForm === null && (
                <div style={{ padding: "2rem", textAlign: "center" }}>Chargement...</div>
            )}
        </div>
    );
}